import React, { useCallback, useRef } from 'react';
import { CollectionSelectionController } from '../types';
import { useLongPressSelection } from './useLongPressSelection';

export function useCollectionEntryInteraction({
  id,
  label,
  selection,
  onOpen,
  visibleIds,
}: {
  id: string;
  label: string;
  selection: CollectionSelectionController;
  onOpen: () => void;
  visibleIds?: string[];
}) {
  const selected = selection.isSelected(id);
  const isDragSelecting = useRef(false);

  const startDragSelection = useCallback(() => {
    isDragSelecting.current = true;

    const handleTouchMove = (event: TouchEvent) => {
      if (!isDragSelecting.current || !visibleIds || visibleIds.length === 0)
        return;
      const touch = event.touches[0];
      if (!touch) return;

      if (event.cancelable) {
        event.preventDefault();
      }

      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      const targetId = element
        ?.closest('[data-item-id]')
        ?.getAttribute('data-item-id');

      if (targetId && visibleIds.includes(targetId)) {
        selection.selectRange(targetId, visibleIds);
      }
    };

    const handleTouchEnd = () => {
      isDragSelecting.current = false;
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
    };

    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchEnd);
  }, [selection, visibleIds]);

  const handleLongPress = useCallback(() => {
    if (selection.disabled) return;
    selection.toggle(id, { visibleIds });
    startDragSelection();
  }, [id, selection, visibleIds, startDragSelection]);

  const press = useLongPressSelection(handleLongPress);

  const activate = (event?: React.MouseEvent) => {
    const isShift = Boolean(event?.shiftKey);
    press.activate(() => {
      if (
        !selection.disabled &&
        isShift &&
        visibleIds &&
        visibleIds.length > 0
      ) {
        selection.toggle(id, { shiftKey: true, visibleIds });
      } else if (selection.active) {
        selection.toggle(id, { visibleIds });
      } else {
        onOpen();
      }
    });
  };

  return {
    selected,
    pressing: press.pressing,
    actionProps: {
      'aria-label': selection.active
        ? `${selected ? 'Deselect' : 'Select'} ${label}`
        : `Open ${label}`,
      'aria-pressed': selection.active ? selected : undefined,
      onClick: (event: React.MouseEvent) => activate(event),
      ...(selection.disabled ? {} : press.handlers),
    },
    toggle: (event?: React.MouseEvent) => {
      const isShift = Boolean(event?.shiftKey);
      selection.toggle(id, { shiftKey: isShift, visibleIds });
    },
  };
}
